import { Redirecting } from '@glade-ai/ui';
import { ArrowRight } from '@phosphor-icons/react';
import { IDENTIFIERS, getUrl } from 'helpers/urlsHelper';
import flatten from 'lodash/flatten';
import { useParams } from 'next/navigation';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga';

import EmbeddedAuthForm from '@/components/Auth/EmbeddedAuthForm';
import type NewServiceCard from '@/components/NewServiceCard';
import { SELF_ORIGIN } from '@/configuration/client';
import deserializeQueryStringItem from '@/helpers/deserializeQueryStringItem';
import {
  isConsultationProduct,
  isInPersonProduct,
  isLiteProduct,
  isLiveSessionProduct,
  isSubscriptionProduct,
} from '@/helpers/helper';
import { isFeaturedHandbook, isHandbookProduct } from '@/helpers/isProductType';
import removeNullish from '@/helpers/removeNullish';
import { usePrevious, useToast, useUser } from '@/hooks';
import useCanEditNoodle from '@/hooks/useCanEditNoodle';
import useNoodleApi from '@/hooks/useNoodleApi';
import NewConversationWithCreator from '@/lib/NewConversationWithCreator';
import ProductCard from '@/lib/UserProfile/components/Card/Card';
import { CardType } from '@/lib/UserProfile/types';
import type CreateEditBroadcastModal from '@/modals/CreateEditBroadcastModal';
import { useIsUserInitialized } from '@/providers/Auth';
import { JobType, useJobContext } from '@/providers/Jobs';
import { JobStatus } from '@/providers/Jobs/Context';
import { mixpanelTrack } from '@/providers/Mixpanel';
import * as tsClient from '@/tsClient';
import * as ApiModels from '@/typings/api-models';
import {
  type Asset,
  type Creator,
  type HandbookStep,
  type Price,
  type Product,
  type ProductType,
} from '@/typings/graphql-models';

import NewHandbookPdfContainer from '../UserProfile/components/NewHandbookPdf/HandbookPdfContainer';

import Broadcasts from './Broadcasts';
import type { Comments } from './Broadcasts';
import Bytes from './Bytes';
import Consultations from './Consultations';
import s from './CreatorBySlug.module.scss';
import Header from './Header';
import Services from './Services';

export type ThisCreator = Pick<Creator, 'id' | 'name' | 'slug' | 'oneLiner' | 'countryCode' | 'businessCategory'> & {
  image?: Pick<Asset, 'url'> | null;
  person?: {
    image?: { url: string } | null;
    id: string;
  } | null;
};

export type ThisProduct = Pick<ApiModels.Product, 'id' | 'slug' | 'title' | 'includesBroadcasts' | 'isActive'> & {
  comments?: Comments;
  image?: Pick<Asset, 'url'> | null;
  prices: Array<Pick<ApiModels.Price, 'id' | 'price' | 'isActive'>>;
  productTypes: Pick<ProductType, 'title' | 'noodleProductType' | 'slug'>[];
  initiateWorkflowCTAs: Array<Pick<ApiModels.InitiateWorkflowCTA, 'id' | 'title' | 'CTA' | 'description'>>;
} & Parameters<typeof isSubscriptionProduct>[0] &
  Parameters<typeof NewServiceCard>[0]['product'] &
  Parameters<typeof NewHandbookPdfContainer>[0]['bytes'][number];

export type ThisProductType = Pick<ProductType, 'id' | 'title' | 'noodleProductType'>;

type Props = {
  data: {
    artist?: ThisCreator | null;
    broadcastTiers: Parameters<typeof CreateEditBroadcastModal>[0]['broadcastTiers'];
    products: ThisProduct[];
    productTypes: ThisProductType[];
    featuredProduct?:
      | (Pick<Product, 'id' | 'title' | 'slug'> & {
          handbookSteps?: Array<Pick<HandbookStep, 'id'>>;
          image?: Pick<Asset, 'url'> | null;
          prices?: Array<Pick<Price, 'id'>>;
        })
      | null;
  };
  isWidget: boolean;
};

const CreatorBySlug: React.FC<Props> = ({ data, isWidget }) => {
  const [productData, setProductData] = useState(data);

  const { data: creator, getData: getCreatorFn } = useNoodleApi(tsClient.getCreator);

  const params = useParams();
  const creatorSlug: string = params.creatorSlug as string;

  const [user] = useUser();
  const isUserInitialized = useIsUserInitialized();
  const router = useRouter();

  const prevCustomerView = usePrevious(router.query.customerView);

  const { jobs } = useJobContext();

  const inProgressPhotoUploadJobs = jobs.filter(
    (j) => j.type === JobType.UPLOAD_PROFILE_PHOTO && j.status === JobStatus.IN_PROGRESS,
  ).length;

  const prevInProgressPhotoUploadJobs = usePrevious(inProgressPhotoUploadJobs);

  const isDisabled = Boolean(
    jobs.find((j) => j.type === JobType.CREATE_DEFAULT_PRODUCTS && j.status === JobStatus.IN_PROGRESS),
  );

  const products = useMemo(() => productData?.products || [], [productData]);

  const addToast = useToast();
  const [selectedProduct, setSelectedProduct] = useState<ThisProduct | null>(null);
  const productTypes = productData?.productTypes || [];

  const setTab = (tab: string): void => {
    const routerBasePath = router.asPath.split('?');
    const queryParamsStr = new URLSearchParams({ tab }).toString();
    const urlWithNewTab = `${routerBasePath[0]}?${queryParamsStr}`;
    router.push(urlWithNewTab, undefined, { shallow: true });
  };

  const canEdit = useCanEditNoodle({ creator });

  const broadcast = products.find((product) => product.includesBroadcasts);

  const broadcastComments = broadcast?.comments?.filter(
    (comment) =>
      comment.type === ApiModels.MessageType.PaidBroadcast ||
      comment.type === ApiModels.MessageType.FreeBroadcast ||
      comment.type === ApiModels.MessageType.CommunityPost,
  );
  const magicProduct = productData?.featuredProduct;

  const membershipProducts = products.filter(isSubscriptionProduct);
  const liteProducts = products.filter(isLiteProduct);
  const onlineSessionProducts = products.filter(isLiveSessionProduct);
  const inPersonSessionProducts = products.filter(isInPersonProduct);
  const consultationProducts = products.filter(isConsultationProduct).filter((p) => p.isActive);

  const consultationProduct: ThisProduct | null =
    consultationProducts.length > 0
      ? {
          handbookSteps: [],
          id: consultationProducts[0].id,
          includesBroadcasts: false,
          initiateWorkflowCTAs: [],
          isActive: true,
          prices: flatten(consultationProducts.map((p) => p.prices)) as ThisProduct['prices'],
          productTypes: consultationProducts[0].productTypes,
          showOnOneLink: true,
          slug: consultationProducts[0].productTypes?.[0].slug,
          title: 'Book a consultation',
        }
      : null;

  const connectProducts = [...inPersonSessionProducts, ...liteProducts, ...onlineSessionProducts, ...membershipProducts]
    .filter(removeNullish)
    .filter(
      (product) =>
        canEdit || product.prices.find((price) => price.isActive) || product.initiateWorkflowCTAs?.length > 0,
    );

  const enableServicesTab = connectProducts.some(
    (product) =>
      product.isActive &&
      product.showOnOneLink &&
      (product.prices.some((price) => price.isActive) || product.initiateWorkflowCTAs?.length > 0),
  );

  const allHandbooks = products.filter((p) => p.isActive).filter(isHandbookProduct);
  const featuredHandbooks = allHandbooks.filter(isFeaturedHandbook).reverse();

  const { getData: refetchProductData, fetchingState: productDataFetchingState } = useNoodleApi(
    tsClient.getCreatorProducts,
  );

  const getDefaultTab = (): string => {
    const tabPriority = [
      {
        condition: enableServicesTab,
        name: 'Services',
      },
      { condition: Boolean(consultationProduct), name: 'Bookings' },
      { condition: (broadcastComments?.length ?? 0) > 0, name: 'Broadcasts' },
      { condition: allHandbooks.length > 0, name: 'Bytes' },
      { condition: true, name: 'Chat' },
    ];

    const firstAvailableTab = tabPriority.find((tab) => tab.condition);

    return firstAvailableTab?.name ?? 'Chat';
  };

  const tab = deserializeQueryStringItem(router.query.tab, {
    defaultValue: getDefaultTab(),
    type: 'string',
  });

  const creatorFromProducts: ThisCreator | null = useMemo(() => productData?.artist || null, [productData]);

  useEffect(() => {
    if (creatorSlug) {
      getCreatorFn({ creatorSlug: creatorSlug });
    }
  }, [creatorSlug, getCreatorFn]);

  useEffect(() => {
    products.forEach((product) => {
      ReactGA.plugin.execute('ec', 'addImpression', {
        brand: creator?.name,
        id: product.id,
        name: product.title,
        price: product?.prices[0]?.price,
      });
    });
  }, [productData, creator?.name, products]);

  useEffect(() => {
    if (prevCustomerView && router.query.customerView !== prevCustomerView) {
      addToast(
        useToast.ToastTypeVariants.INFO,
        router.query.customerView === 'true' ? 'This is your customer view.' : 'You are now in edit mode.',
      );
    }
  }, [addToast, router.query.customerView, prevCustomerView]);

  useEffect(() => {
    mixpanelTrack('View creator slug page');
  }, []);

  const refetchData = useCallback(async (): Promise<void> => {
    const { data: newData } = await refetchProductData({ creatorSlug: data.artist?.slug || '' });
    setProductData({
      ...newData,
      artist: newData?.artist?.[0] || null,
      broadcastTiers: data.broadcastTiers || [],
      productTypes: newData?.productTypes[0]?.productTypes || [],
      products: newData?.products || [],
    });
    setSelectedProduct(newData?.products?.find((p) => p.id === selectedProduct?.id) || null);
  }, [data?.artist?.slug, data?.broadcastTiers, refetchProductData, selectedProduct?.id]);

  useEffect(() => {
    // Only refetch when the number of in progress photo uploads changes from nonzero to zero
    if (inProgressPhotoUploadJobs === 0 && (prevInProgressPhotoUploadJobs || 0) > 0) {
      refetchData();
    }
  }, [inProgressPhotoUploadJobs, prevInProgressPhotoUploadJobs, refetchData]);

  const activeConsultationProducts = consultationProducts.filter(
    (product) =>
      product.isActive &&
      product.prices.length > 0 &&
      product.prices.some((price) => price.isActive) &&
      product.showOnOneLink,
  );

  useEffect(() => {
    if (tab === 'Bookings') {
      mixpanelTrack('Viewed Consultations Page', {
        creatorSlug,
        userId: user?.id,
      });
    }
  }, [tab]);

  // Deterministically redirect authenticated users away from the Profile tab.
  // The sign-in forms' own redirects can race back here (returnTo falls back to routerAsPath),
  // so this effect provides a reliable fallback that always navigates to the user profile.
  const isAuthenticated = isUserInitialized && user && !user.isAnonymous;
  useEffect(() => {
    if (tab === 'Profile' && isAuthenticated && creatorSlug) {
      void router.push(getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug }));
    }
  }, [tab, isAuthenticated, creatorSlug, router]);

  return (
    <div className={s['creator-page__wrapper']}>
      <Header
        isWidget={isWidget}
        tab={tab}
        setTab={setTab}
        creator={creator}
        creatorFromProducts={creatorFromProducts}
        canEdit={canEdit}
        consultationProduct={consultationProduct}
        enableServicesTab={enableServicesTab}
        broadcastComments={broadcastComments}
        allHandbooks={allHandbooks}
      />
      <div className={s[`creator-page__content-wrapper${(tab === 'Chat' && '-chat') || ''}`]}>
        {tab === 'Services' && consultationProduct && (
          <div className={s.consultationLink} onClick={() => setTab('Bookings')}>
            <div className={s.consultationLink__gradient} />
            <div className={s.consultationLink__title}>
              <span>Book a Consultation</span>
              <ArrowRight size={16} weight='bold' />
            </div>
            <div className={s.consultationLink__options}>
              {activeConsultationProducts.length} {activeConsultationProducts.length === 1 ? 'option' : 'options'}
            </div>
          </div>
        )}

        {isWidget && creator && magicProduct && (
          <ProductCard
            className={s['creator-page__magic-product']}
            type={CardType.Plan}
            link={`${SELF_ORIGIN}${getUrl(IDENTIFIERS.PRODUCT_PURCHASE, {
              creatorSlug: creator.slug,
              priceId: magicProduct.prices?.[0]?.id,
              productSlug: magicProduct?.slug,
            })}`}
            isFree={true}
            inContainer={false}
            image={magicProduct?.image?.url}
            title={magicProduct?.title || ''}
            steps={magicProduct?.handbookSteps?.length}
            price={null}
          />
        )}
        <div className={s.connect}>
          {creator && isUserInitialized && (
            <div style={{ display: tab === 'Chat' ? 'unset' : 'none' }}>
              <NewConversationWithCreator isAnonymous={!user} isEmbedded creatorSlug={creator.slug} />
            </div>
          )}
          {tab === 'Services' && creator && (
            <Services
              creator={creator}
              isFetching={productDataFetchingState.isFetching}
              connectProducts={connectProducts}
              canEdit={canEdit}
            />
          )}
          {tab === 'Bookings' && creator && (
            <Consultations consultationProducts={consultationProducts} creatorSlug={creator.slug} />
          )}
          {tab === 'Bytes' && creator && (
            <Bytes
              productTypes={productTypes}
              allHandbooks={allHandbooks}
              creatorSlug={creator.slug}
              canEdit={canEdit}
            />
          )}
          {tab === 'Broadcasts' && creator && (
            <Broadcasts
              membershipProducts={membershipProducts}
              broadcast={broadcast}
              broadcastComments={broadcastComments}
              broadcastTiers={data.broadcastTiers}
              canEdit={canEdit}
              creator={creator}
              refetchData={refetchData}
            />
          )}
          {creator && <NewHandbookPdfContainer bytes={featuredHandbooks} creatorSlug={creator.slug} tab={tab} />}{' '}
          {/* Might render on both Services and Bytes tabs */}
        </div>
        {tab === 'Profile' &&
          (!isUserInitialized || isAuthenticated ? (
            <Redirecting title='userProfile.home.empty.title' description='userProfile.home.empty.description' />
          ) : (
            <EmbeddedAuthForm creatorSlug={creator?.slug} defaultView='signUp' isCreatorView={true} />
          ))}
        {isDisabled && (
          <p className='caption' style={{ color: 'var(--color-gray-75)', textAlign: 'center' }}>
            We’re still getting this set up. Just a moment...
          </p>
        )}
      </div>
    </div>
  );
};

export default CreatorBySlug;
